import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { identificationApi } from '../identificationApi';
import type { IdentificationType } from '../identificationTypes';
import { sampleIdentificationType } from '../utils/sampleIdentificationType';

/**
 * Create or edit one identification type.
 *
 * Three fields, and no position: the order these appear in on the personnel form is assigned
 * by the server and changed by moving the row, not by typing a number here — a rename is not a
 * reorder.
 */
export function IdentificationTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: IdentificationType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      code: code.trim() || null,
      description: description.trim() || null,
    };

    try {
      if (editing) await identificationApi.update(editing.id, request);
      else await identificationApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this type.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'identification-type' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleIdentificationType();

    setName(sample.name);
    setCode(sample.code);
    setDescription(sample.description);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit identification type' : 'New identification type'}
      size="lg"
      id="identification-type-modal"
      autogenerate={editing === null ? { onGenerate: generate, disabled: saving } : undefined}
      confirm={{
        id: 'identification-type-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="identification-type-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            id="identification-type-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="identification-type-name"
          label="Name"
          required
          description="What the field is called on the personnel form — SSS number, PRC licence."
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={name.trim() ? error?.fieldError('name') : 'Enter a name.'}
          disabled={saving}
        />

        <TextField
          id="identification-type-code"
          label="Code"
          description="A short label for the list — SSS, TIN. Nothing quotes it, so it is free to change."
          value={code}
          onChange={(e) => setCode(e.target.value)}
          error={error?.fieldError('code')}
          disabled={saving}
        />

        <TextField
          id="identification-type-description"
          label="Description"
          description="One line saying what this number is. Shown as the field's help text."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={error?.fieldError('description')}
          disabled={saving}
        />

        {/*
          Said on the form rather than on the tick: a new type appears on every personnel form
          from the moment it is saved, which is a change to what the whole module collects.
        */}
        <p className="text-xs text-slate-500">
          A new type becomes a field on the Identification step of every personnel record. Deleting
          one removes the field and leaves the numbers already recorded against it alone.
        </p>
      </div>
    </Modal>
  );
}
